import React, {useContext, useMemo} from 'react';
import {Card, Col, Empty, Progress, Row, Space, Tag, Tooltip, Typography} from 'antd';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {AuthContext} from '../../../../shared/contexts/authContext';
import {useItemKrTextData} from '../../../probability/useItemData';
import {useColorTargetData, useMergeEventBlockMainData} from '../../../logs/useTableData';
import {resolveMergeBlockImageUrl, useMergeBlockImageMap} from '../../../../shared/hooks/useMergeBlockImageMap';
import {getItemName} from '../../../../shared/utils/basic';
import IsLoading from '../../../../shared/components/IsLoading';
import ErrorPage from '../../../../shared/components/ErrorPage';

const {Text} = Typography;

const TEXT = {
    target: "먼지 아이템",
    first_reward: "첫 달성",
    clear_reward: "{{round}}라운드 클리어 보상",
    color_1: "파랑",
    color_2: "빨강",
    color_3: "초록",
    no_data: "대청소 이벤트 데이터가 없습니다.",
    event_id: "이벤트 ID",
    ended: "종료",
    round: "라운드",
    infinite_mode: "무한모드",
    infinite_gauge: "무한모드 진행 게이지",
    infinite_reward: "무한모드 보상",
    infinite_reward_count: "보상 획득 횟수",
    emblem: "엠블럼 수집 현황",
    emblem_rank: "{{rank}}단계",
    boss: "보스",
    lane: "{{lane}}번 먼지",
    sequence: "{{seq}}번째",
    hp: "잔여 체력",
    cleared: "청소 완료",
    board: "머지 보드",
    box_1: "반 열린 박스",
    box_2: "덮인 박스",
    clean_count: "청소 횟수",
    move_multiplier: "소모 배수",
    special_items: "특수 아이템",
    reward_board: "추가 보상 보드",
    pending_rewards: "저장 보상",
};
const L = (key, opt = {}) => (TEXT[key] || key).replace(/{{(\w+)}}/g, (_, k) => opt[k]);

const W = 7;
const BLOCK_TYPE = 7;
const BOSS_TARGET = 901;
const KIND = {EMPTY: 0, NORMAL: 1};
// 색(slotTypes 값) 1:파랑 2:빨강 3:초록
const COLOR_BG = {1: '#e3f0ff', 2: '#ffe3e3', 3: '#e3f7e3'};
const COLOR_BAR = {1: '#1677ff', 2: '#ff4d4f', 3: '#52c41a'};
const BOX_BORDER = {1: '2px dashed #a0703c', 2: '2px solid #a0703c'};

// 저장 index = y*7+x (y=0 이 맨 아래 행). 화면 좌상단부터 그리도록 행을 뒤집는다.
export const toDisplayOrder = (cells, height) =>
    Array.from({length: W * height}, (_, i) => cells?.[(height - 1 - Math.floor(i / W)) * W + i % W]);

// targetId = 라운드*1000 + (레인+1)*100 + 순서, 보스 = 라운드*1000 + 901
export const decodeTargetId = (targetId) => {
    const round = Math.floor(targetId / 1000);
    const rest = targetId % 1000;
    if (rest === BOSS_TARGET) return {round, boss: true};
    return {round, lane: Math.floor(rest / 100) - 1, seq: rest % 100};
};

const Cell = ({image, label, sub, bg, border, tooltip}) => (
    <Tooltip title={tooltip}>
        <div style={{
            aspectRatio: '1 / 1', borderRadius: 6, background: bg || '#f5f5f5', border: border || '1px solid #e0e0e0',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', fontSize: 11, lineHeight: 1.2, textAlign: 'center', color: '#333', minWidth: 0,
        }}>
            {image && <img src={image} alt="" style={{width: '70%', height: '55%', objectFit: 'contain'}}
                           onError={e => { e.currentTarget.style.display = 'none'; }}/>}
            {label != null && <b>{label}</b>}
            {sub && <span style={{fontSize: 10, color: '#666'}}>{sub}</span>}
        </div>
    </Tooltip>
);

const GRID_WIDTH = 460;
// small 카드 좌우 padding 12 + border 1
const COL_WIDTH = GRID_WIDTH + 26;

const Grid = ({children, cols = W}) => (
    <div style={{display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 3, maxWidth: GRID_WIDTH}}>{children}</div>
);

const ColorTargetBoard = () => {
    const {playerData} = useContext(PlayerContext);
    const {currentUser} = useContext(AuthContext);
    const {data: imageMap} = useMergeBlockImageMap();
    const {data: blockMain, isLoading: l1, error: e1} = useMergeEventBlockMainData();
    const {data: ctTable, isLoading: l2, error: e2} = useColorTargetData();
    const {data: itemTextData, isLoading: l3, error: e3} = useItemKrTextData();

    const state = playerData?.playerInfo?.mergeEventData?.ActiveMergeEventState;
    const ct = state?.ColorTargetState;
    const eventId = state?.id > 0 ? state.id : state?.backupID;

    // 일반 블록: (blockGIdx, level) → 블록
    const normalBlocks = useMemo(() => {
        const map = {};
        Object.values(blockMain || {}).forEach(b => {
            if (b.mergeEventIdx === eventId) map[`${b.blockGIdx}_${b.blockLevel}`] = b;
        });
        return map;
    }, [blockMain, eventId]);

    if (l1 || l2 || l3) return <IsLoading/>;
    if (e1 || e2 || e3) return <ErrorPage error={e1 || e2 || e3}/>;
    if (!ct?.initialized) return <Empty description={L('no_data')}/>;

    const texts = itemTextData?.[currentUser.target] || [];
    const itemName = (id, type = BLOCK_TYPE) => getItemName(texts, id, type);
    const blockImage = (block) => resolveMergeBlockImageUrl(block?.resourcePath, imageMap);
    const colorName = (gIdx) => (COLOR_BG[gIdx] ? L(`color_${gIdx}`) : '');

    const set = ctTable.set?.[eventId];
    const emblems = ctTable.emblem.filter(e => e.mergeEventIdx === eventId).sort((a, b) => a.rank - b.rank);
    const enemyRow = (round) => ctTable.enemy.find(e => e.mergeEventIdx === eventId && e.eventRound === round);
    const emblemList = emblems.length ? emblems : (ct.emblemCounts || []).map((_, i) => ({rank: i + 1}));
    const gaugeNeed = set?.infiniteGaugeNeed || 0;

    // 6. 먼지 아이템
    const targetCard = (key, title, targetId, hp, gIdx) => {
        const bg = COLOR_BG[gIdx];
        if (!targetId) {
            return <Card key={key} size="small" title={title} style={{background: bg}}><Tag color="green">{L('cleared')}</Tag></Card>;
        }
        const {round, boss, lane, seq} = decodeTargetId(targetId);
        const row = enemyRow(round);
        const maxHp = boss ? row?.bossHp : row?.hp?.[lane]?.[seq - 1];
        return (
            <Card key={key} size="small" title={title} style={{background: bg}}>
                <div>{!boss && L('sequence', {seq})} <Text type="secondary">({targetId})</Text></div>
                <div>{L('hp')}: {hp} / {maxHp ?? '-'}</div>
                {maxHp > 0 && <Progress percent={Math.round(hp / maxHp * 100)} showInfo={false} size="small" strokeColor={COLOR_BAR[gIdx]}/>}
            </Card>
        );
    };

    // 7. 머지 보드
    const boardCell = (cell, i) => {
        if (!cell || cell.kind === KIND.EMPTY) return <Cell key={i}/>;
        const border = BOX_BORDER[cell.box];
        const boxText = cell.box ? L(`box_${cell.box}`) : '';
        if (cell.kind === KIND.NORMAL) {
            const gIdx = ct.slotTypes?.[cell.slot];
            const block = normalBlocks[`${gIdx}_${cell.level}`];
            return <Cell key={i} image={blockImage(block)} label={`Lv${cell.level}`} sub={boxText} bg={COLOR_BG[gIdx]} border={border}
                         tooltip={`${itemName(block?.index)} (${block?.index ?? '-'}) ${boxText}`}/>;
        }
        const name = itemName(cell.specialId);
        return <Cell key={i} image={blockImage(blockMain[cell.specialId])} label={name} sub={boxText} bg="#efe3ff" border={border}
                     tooltip={`${name} (${cell.specialId}) ${boxText}`}/>;
    };

    const rewardCell = (index, i) => index
        ? <Cell key={i} image={blockImage(blockMain[index])} label={itemName(index)} bg="#fff" tooltip={`${itemName(index)} (${index})`}/>
        : <Cell key={i}/>;

    const rewardText = (r) => `${itemName(r.id, r.type)} x ${r.quantity}`;
    const boardHeight = Math.ceil((ct.board?.length || 42) / W);
    const rewardHeight = Math.ceil((ct.rewardBoard?.length || 35) / W);

    return (
        <Space direction="vertical" style={{width: '100%'}} size="middle">
            <Space wrap>
                <Tag color="blue">{L('event_id')}: {eventId}{state.id > 0 ? '' : ` (${L('ended')})`}</Tag>
                <Tag>{L('round')}: {ct.round}</Tag>
                {ct.isInfinite && <Tag color="purple">{L('infinite_mode')}</Tag>}
            </Space>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start'}}>
                {/* 12. 추가 보상 보드 (좌측 붉은 영역) */}
                <div style={{width: COL_WIDTH, maxWidth: '100%'}}>
                    <Card size="small" title={L('reward_board')}>
                        <Grid>{toDisplayOrder(ct.rewardBoard, rewardHeight).map(rewardCell)}</Grid>
                        <div style={{marginTop: 12}}>
                            <Text strong>{L('pending_rewards')} ({ct.pendingRewards?.length || 0})</Text>
                            <div style={{display: 'flex', gap: 4, overflowX: 'auto', marginTop: 6}}>
                                {(ct.pendingRewards || []).map((index, i) => (
                                    <div key={i} style={{width: 56, flexShrink: 0}}>{rewardCell(index, i)}</div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>

                <div style={{width: COL_WIDTH, maxWidth: '100%'}}>
                    <Space direction="vertical" style={{width: '100%'}} size="middle">
                        {/* 2·3. 무한모드 */}
                        <Card size="small" title={L('infinite_gauge')}>
                            <div>{ct.infiniteGauge} / {gaugeNeed || '-'}</div>
                            {gaugeNeed > 0 && <Progress percent={Math.round(ct.infiniteGauge / gaugeNeed * 100)}/>}
                            <div>{L('infinite_reward')}: {(set?.infiniteReward || []).map(rewardText).join(', ') || '-'}</div>
                            <div>{L('infinite_reward_count')}: {ct.infiniteRewardCount}</div>
                        </Card>

                        {/* 5. 엠블럼 */}
                        <Card size="small" title={L('emblem')}>
                            <Grid cols={emblemList.length || W}>
                                {emblemList.map(e => (
                                    <Cell key={e.rank} image={resolveMergeBlockImageUrl(e.iconPath, imageMap)}
                                          label={L('emblem_rank', {rank: e.rank})} sub={`x${ct.emblemCounts?.[e.rank - 1] ?? 0}`}
                                          bg={ct.emblemCounts?.[e.rank - 1] > 0 ? '#fff6cc' : undefined}/>
                                ))}
                            </Grid>
                            <div style={{marginTop: 8, fontSize: 12}}>
                                {emblems.map(e => (
                                    <div key={e.rank}>
                                        <b>{L('emblem_rank', {rank: e.rank})}</b>: {(e.reward || []).map(rewardText).join(', ') || '-'}
                                        {e.firstReward?.length > 0 && <Text type="secondary"> ({L('first_reward')}: {e.firstReward.map(rewardText).join(', ')})</Text>}
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* 6. 먼지 아이템 */}
                        <Card size="small" title={L('target')}>
                        <Row gutter={8}>
                            {ct.phase === 1
                                ? <Col span={24}>{targetCard('boss', L('boss'), ct.bossTargetId, ct.bossHp)}</Col>
                                : (ct.lanes || []).map((lane, i) => (
                                    <Col span={8} key={i}>
                                        {targetCard(i, colorName(ct.slotTypes?.[i]) || L('lane', {lane: i + 1}), lane.targetId, lane.hp, ct.slotTypes?.[i])}
                                    </Col>
                                ))}
                        </Row>
                        <div style={{marginTop: 8}}>{L('clear_reward', {round: ct.round})}: {(enemyRow(ct.round)?.reward || []).map(rewardText).join(', ') || '-'}</div>
                        </Card>

                        {/* 7. 머지 보드 */}
                        <Card size="small" title={L('board')}>
                            <Grid>{toDisplayOrder(ct.board, boardHeight).map(boardCell)}</Grid>
                        </Card>

                        {/* 9·11. 청소 횟수 / 특수 아이템 */}
                        <Card size="small">
                            <div>{L('clean_count')}: <b>{state.currencyPoint}</b> ({L('move_multiplier')} x{ct.moveMultiplier})</div>
                            <div>
                                {L('special_items')}: {Object.entries(ct.ownedSpecialBlocks || {})
                                    .map(([index, count]) => `${itemName(index)} x ${count}`).join(', ') || '-'}
                            </div>
                        </Card>
                    </Space>
                </div>
            </div>
        </Space>
    );
};

export default ColorTargetBoard;
